/**
 * Investigation export utilities — WP-S15.2.
 *
 * Exports investigation data in 4 formats:
 * - JSON: Full investigation (entities, relationships, provenance, assessment)
 * - CSV: Flat entity and relationship tables
 * - Markdown: Formatted assessment with sections + citations
 * - STIX 2.1: Entity → STIX SDO/SRO mapping
 *
 * All exports include provenance chains and audit metadata.
 */

import type { Entity, Relationship, EntityType } from '@/intelligence/entities/types';
import type { DraftAssessment } from '@/intelligence/investigation/types';
import type { NormalizedEvent } from '@/sources/base-source';
import type { ExportFormat } from './types';
import type { CollectionLogSummary } from '@/intelligence/investigation/collection-log';

// ---------------------------------------------------------------------------
// Investigation export data shape
// ---------------------------------------------------------------------------

export interface InvestigationExportData {
  investigationId: string;
  title: string;
  query: string;
  tier: string;
  deskId: string;
  entities: Entity[];
  relationships: Relationship[];
  assessment: DraftAssessment | null;
  auditLog: CollectionLogSummary | null;
  exportedAt: string;
}

// ---------------------------------------------------------------------------
// JSON export — full investigation data
// ---------------------------------------------------------------------------

export function exportInvestigationJSON(data: InvestigationExportData): Blob {
  const json = JSON.stringify(data, null, 2);
  return new Blob([json], { type: 'application/json' });
}

// ---------------------------------------------------------------------------
// CSV export — flat entity and relationship tables
// ---------------------------------------------------------------------------

function escapeCSV(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return '"' + value.replace(/"/g, '""') + '"';
  }
  return value;
}

export function exportEntitiesCSV(entities: Entity[]): Blob {
  const headers = [
    'id', 'type', 'name', 'description', 'confidence',
    'nato_rating', 'corroboration_count', 'source_url',
    'extraction_quote', 'first_seen', 'last_seen',
  ];

  const rows = [headers.join(',')];

  for (const e of entities) {
    rows.push([
      escapeCSV(e.id),
      escapeCSV(e.type),
      escapeCSV(e.name),
      escapeCSV(e.description ?? ''),
      String(e.confidence),
      escapeCSV(e.corroboration.natoRating),
      String(e.corroboration.count),
      escapeCSV(e.provenance.sourceUrl),
      escapeCSV(e.provenance.extractionQuote),
      escapeCSV(e.firstSeen),
      escapeCSV(e.lastSeen),
    ].join(','));
  }

  return new Blob([rows.join('\n')], { type: 'text/csv' });
}

export function exportRelationshipsCSV(relationships: Relationship[]): Blob {
  const headers = [
    'id', 'source_entity_id', 'target_entity_id', 'type',
    'confidence', 'first_seen', 'last_seen',
  ];

  const rows = [headers.join(',')];

  for (const r of relationships) {
    rows.push([
      escapeCSV(r.id),
      escapeCSV(r.sourceEntityId),
      escapeCSV(r.targetEntityId),
      escapeCSV(r.type),
      String(r.confidence),
      escapeCSV(r.firstSeen),
      escapeCSV(r.lastSeen),
    ].join(','));
  }

  return new Blob([rows.join('\n')], { type: 'text/csv' });
}

/**
 * Export full investigation as CSV (entities + relationships in separate sections).
 */
export function exportInvestigationCSV(data: InvestigationExportData): Blob {
  const parts: string[] = [];

  parts.push('# ENTITIES');
  parts.push(buildEntitiesCSVString(data.entities));
  parts.push('');
  parts.push('# RELATIONSHIPS');
  parts.push(buildRelationshipsCSVString(data.relationships));

  return new Blob([parts.join('\n')], { type: 'text/csv' });
}

function buildEntitiesCSVString(entities: Entity[]): string {
  const headers = [
    'id', 'type', 'name', 'description', 'confidence',
    'nato_rating', 'corroboration_count', 'source_url',
    'extraction_quote', 'first_seen', 'last_seen',
  ];
  const rows = [headers.join(',')];
  for (const e of entities) {
    rows.push([
      escapeCSV(e.id), escapeCSV(e.type), escapeCSV(e.name),
      escapeCSV(e.description ?? ''), String(e.confidence),
      escapeCSV(e.corroboration.natoRating), String(e.corroboration.count),
      escapeCSV(e.provenance.sourceUrl), escapeCSV(e.provenance.extractionQuote),
      escapeCSV(e.firstSeen), escapeCSV(e.lastSeen),
    ].join(','));
  }
  return rows.join('\n');
}

function buildRelationshipsCSVString(relationships: Relationship[]): string {
  const headers = [
    'id', 'source_entity_id', 'target_entity_id', 'type',
    'confidence', 'first_seen', 'last_seen',
  ];
  const rows = [headers.join(',')];
  for (const r of relationships) {
    rows.push([
      escapeCSV(r.id), escapeCSV(r.sourceEntityId), escapeCSV(r.targetEntityId),
      escapeCSV(r.type), String(r.confidence), escapeCSV(r.firstSeen), escapeCSV(r.lastSeen),
    ].join(','));
  }
  return rows.join('\n');
}

// ---------------------------------------------------------------------------
// Markdown export — formatted assessment
// ---------------------------------------------------------------------------

export function exportInvestigationMarkdown(data: InvestigationExportData): Blob {
  const lines: string[] = [];

  lines.push(`# Investigation: ${data.title}`);
  lines.push('');
  lines.push(`**Query:** ${data.query}`);
  lines.push(`**Tier:** ${data.tier}`);
  lines.push(`**Desk:** ${data.deskId}`);
  lines.push(`**Entities:** ${data.entities.length}`);
  lines.push(`**Relationships:** ${data.relationships.length}`);
  lines.push(`**Exported:** ${data.exportedAt}`);
  lines.push('');

  // Assessment
  if (data.assessment) {
    lines.push('---');
    lines.push('');
    lines.push('> **MACHINE-GENERATED DRAFT — REQUIRES ANALYST REVIEW**');
    lines.push('');
    lines.push('## Executive Summary');
    lines.push('');
    lines.push(data.assessment.executiveSummary);
    lines.push('');

    for (const section of data.assessment.sections) {
      lines.push(`## ${section.title}`);
      lines.push('');
      lines.push(section.content);

      if (section.citations.length > 0) {
        lines.push('');
        lines.push(`*Referenced entities: ${section.citations.join(', ')}*`);
      }
      lines.push('');
    }

    lines.push(`*Model: ${data.assessment.modelUsed} | Tokens: ${data.assessment.tokenCost} | Generated: ${data.assessment.generatedAt}*`);
    lines.push('');
  }

  // Entity table
  lines.push('---');
  lines.push('');
  lines.push('## Entity Inventory');
  lines.push('');
  lines.push('| ID | Type | Name | NATO | Confidence | Sources |');
  lines.push('|---|---|---|---|---|---|');

  for (const e of data.entities) {
    lines.push(
      `| ${e.id} | ${e.type} | ${e.name} | ${e.corroboration.natoRating} | ${e.confidence.toFixed(2)} | ${e.corroboration.count} |`,
    );
  }

  lines.push('');

  // Relationship table
  if (data.relationships.length > 0) {
    lines.push('## Relationships');
    lines.push('');
    lines.push('| Source | Relationship | Target | Confidence |');
    lines.push('|---|---|---|---|');

    // Build name lookup
    const nameMap = new Map<string, string>();
    for (const e of data.entities) nameMap.set(e.id, e.name);

    for (const r of data.relationships) {
      const src = nameMap.get(r.sourceEntityId) ?? r.sourceEntityId;
      const tgt = nameMap.get(r.targetEntityId) ?? r.targetEntityId;
      lines.push(`| ${src} | ${r.type} | ${tgt} | ${r.confidence.toFixed(2)} |`);
    }

    lines.push('');
  }

  // Audit summary
  if (data.auditLog) {
    lines.push('## Audit Trail Summary');
    lines.push('');
    lines.push(`- Tasks dispatched: ${data.auditLog.tasksDispatched}`);
    lines.push(`- Tasks completed: ${data.auditLog.tasksCompleted}`);
    lines.push(`- Tasks failed: ${data.auditLog.tasksFailed}`);
    lines.push(`- Entities extracted: ${data.auditLog.entitiesExtracted}`);
    lines.push(`- Entities rejected: ${data.auditLog.entitiesRejected}`);
    lines.push(`- LLM calls: ${data.auditLog.llmCalls.length}`);

    if (data.auditLog.errors.length > 0) {
      lines.push('');
      lines.push('### Errors');
      for (const err of data.auditLog.errors) {
        lines.push(`- [${err.timestamp}] ${err.state}: ${err.error}`);
      }
    }
  }

  return new Blob([lines.join('\n')], { type: 'text/markdown' });
}

// ---------------------------------------------------------------------------
// STIX 2.1 export — Entity → SDO/SRO mapping
// ---------------------------------------------------------------------------

/** Map Sentinel entity types to STIX 2.1 SDO types */
const ENTITY_TO_STIX_TYPE: Record<EntityType, string> = {
  PERSON: 'identity',
  ORGANIZATION: 'identity',
  LOCATION: 'location',
  ASSET: 'infrastructure',
  EVENT: 'observed-data',
  INDICATOR: 'indicator',
  SOURCE: 'identity',
};

/** Map Sentinel entity types to STIX identity_class */
const ENTITY_TO_IDENTITY_CLASS: Partial<Record<EntityType, string>> = {
  PERSON: 'individual',
  ORGANIZATION: 'organization',
  SOURCE: 'system',
};

function generateSTIXId(type: string): string {
  return `${type}--${crypto.randomUUID()}`;
}

export function exportInvestigationSTIX(data: InvestigationExportData): Blob {
  const bundle: Record<string, unknown> = {
    type: 'bundle',
    id: generateSTIXId('bundle'),
    spec_version: '2.1',
    objects: [] as Record<string, unknown>[],
  };

  const objects = bundle.objects as Record<string, unknown>[];
  const entityStixIdMap = new Map<string, string>();

  // Convert entities to STIX SDOs
  for (const entity of data.entities) {
    const stixType = ENTITY_TO_STIX_TYPE[entity.type];
    const stixId = generateSTIXId(stixType);
    entityStixIdMap.set(entity.id, stixId);

    const sdo: Record<string, unknown> = {
      type: stixType,
      id: stixId,
      spec_version: '2.1',
      created: entity.firstSeen,
      modified: entity.lastSeen,
      confidence: Math.round(entity.confidence * 100),
      external_references: [{
        source_name: 'sentinel',
        external_id: entity.id,
        url: entity.provenance.sourceUrl || undefined,
        description: entity.provenance.extractionQuote || undefined,
      }],
    };

    // Type-specific fields
    if (stixType === 'identity') {
      sdo.name = entity.name;
      sdo.description = entity.description;
      sdo.identity_class = ENTITY_TO_IDENTITY_CLASS[entity.type] ?? 'unspecified';
    } else if (stixType === 'location') {
      sdo.name = entity.name;
      sdo.description = entity.description;
      const coords = entity.properties.coordinates as [number, number] | undefined;
      if (coords) {
        // Coordinates stored as [lng, lat] (GeoJSON convention)
        sdo.latitude = coords[1];
        sdo.longitude = coords[0];
      }
    } else if (stixType === 'infrastructure') {
      sdo.name = entity.name;
      sdo.description = entity.description;
      sdo.infrastructure_types = [entity.properties.asset_type ?? 'unknown'];
    } else if (stixType === 'indicator') {
      sdo.name = entity.name;
      sdo.description = entity.description;
      sdo.pattern_type = 'stix';
      sdo.pattern = `[sentinel:indicator = '${entity.name.replace(/'/g, "\\'")}']`;
      sdo.valid_from = entity.firstSeen;
    } else if (stixType === 'observed-data') {
      sdo.first_observed = entity.firstSeen;
      sdo.last_observed = entity.lastSeen;
      sdo.number_observed = 1;
    }

    objects.push(sdo);
  }

  // Convert relationships to STIX SROs
  for (const rel of data.relationships) {
    const sourceRef = entityStixIdMap.get(rel.sourceEntityId);
    const targetRef = entityStixIdMap.get(rel.targetEntityId);
    if (!sourceRef || !targetRef) continue;

    objects.push({
      type: 'relationship',
      id: generateSTIXId('relationship'),
      spec_version: '2.1',
      relationship_type: rel.type.replace(/_/g, '-'),
      source_ref: sourceRef,
      target_ref: targetRef,
      confidence: Math.round(rel.confidence * 100),
      created: rel.firstSeen,
      modified: rel.lastSeen,
    });
  }

  const json = JSON.stringify(bundle, null, 2);
  return new Blob([json], { type: 'application/json' });
}

// ---------------------------------------------------------------------------
// Unified export function
// ---------------------------------------------------------------------------

/**
 * Export investigation data in the specified format.
 */
export function exportInvestigation(
  data: InvestigationExportData,
  formatId: string,
): Blob {
  switch (formatId) {
    case 'json':
      return exportInvestigationJSON(data);
    case 'csv':
      return exportInvestigationCSV(data);
    case 'markdown':
      return exportInvestigationMarkdown(data);
    case 'stix':
      return exportInvestigationSTIX(data);
    default:
      throw new Error(`Unsupported export format: ${formatId}`);
  }
}

// ---------------------------------------------------------------------------
// Legacy stubs (preserved for backward compatibility with existing callers)
// ---------------------------------------------------------------------------

/** Export events to the specified format. */
export async function exportEvents(
  _events: NormalizedEvent[],
  _format: ExportFormat,
): Promise<Blob> {
  return new Blob([''], { type: 'text/plain' });
}

/** Export entities to the specified format. */
export async function exportEntities(
  entities: Entity[],
  format: ExportFormat,
): Promise<Blob> {
  if (format.id === 'csv') return exportEntitiesCSV(entities);
  if (format.id === 'json') return new Blob([JSON.stringify(entities, null, 2)], { type: 'application/json' });
  return new Blob([''], { type: 'text/plain' });
}

/** Export to STIX 2.1 bundle. */
export async function exportStix(
  _events: NormalizedEvent[],
  entities: Entity[],
): Promise<Blob> {
  return exportInvestigationSTIX({
    investigationId: '',
    title: 'Entity Export',
    query: '',
    tier: '',
    deskId: '',
    entities,
    relationships: [],
    assessment: null,
    auditLog: null,
    exportedAt: new Date().toISOString(),
  });
}
